import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, MonoTypeOperatorFunction, NEVER, Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { BackendFeatureService } from '../backend-feature.service';
import { BackendInterceptor } from '../backend-interceptor/backend-interceptor.service';

export const LIVE_EVENTS_FEATURE_ID = 'gung-live-events';

/** Mirrors the type constants in EntityChangedEvent (gung-core). */
export enum LiveEventType {
  ORDER = 'order',
  OFFER = 'offer',
  INVOICE = 'invoice'
}

/** RESYNC: synthetic, sent once a topic's subscription is active on the server, so the caller refetches past any missed change. */
export type LiveEventAction = 'CREATED' | 'UPDATED' | 'DELETED' | 'RESYNC';

export interface LiveEvent {
  /** Plain string: a newer backend may send types this frontend doesn't know yet. */
  type: string;
  key: string;
  action: LiveEventAction;
  timestamp: string;
  topics: string[];
}

export type LiveEventStatus = 'inactive' | 'connecting' | 'open' | 'unavailable';

/** Messages between the tabs of one browser. tabId set on 'resync' targets a single tab. */
export type LiveEventTabMessage =
  | { kind: 'leader' }
  | { kind: 'collect' }
  | { kind: 'topics'; tabId: string; topics: string[]; visible: boolean; resync: boolean }
  | { kind: 'event'; event: LiveEvent }
  | { kind: 'resync'; topics: string[]; tabId?: string }
  | { kind: 'status'; status: LiveEventStatus; hiddenSince: number | null };

interface RemoteTab {
  topics: Set<string>;
  visible: boolean;
  missedCollects: number;
}

/** Must match LiveEvent.escapeKey in the backend: ids can then never look like a scope or the wildcard. */
export function escapeLiveEventKey(key: string): string {
  return key.replace(/%/g, '%25').replace(/:/g, '%3A').replace(/\*/g, '%2A');
}

/**
 * Change notifications over a single server-sent events stream per browser, shared by every subscriber in every tab.
 *
 * One tab (the leader, elected through the Web Locks API) owns the stream and subscribes the union of all tabs' topics;
 * the others send it their topics and receive its events over a BroadcastChannel. When the leader closes, the next
 * tab in line takes over. A leader hidden for longer than the grace period is taken over by a visible tab, since
 * hidden tabs may be frozen. Without Web Locks or BroadcastChannel every tab opens its own stream.
 *
 * Best effort by design: if the backend feature is off, oauth2 is used (EventSource can't send bearer tokens)
 * or the stream can't be established, on() simply never emits. Callers keep their existing fetch behaviour
 * and treat events as an extra "refetch now" hint. Events are thin, so refetch through the normal endpoint.
 * Every topic gets a RESYNC once the server confirms it: on first subscribe and again after any reconnect (hidden tabs,
 * leader change, backend restart, network drop). Changes made before the subscription was active can't be missed that way.
 */
@Injectable({
  providedIn: 'root'
})
export class LiveEventService {
  protected http = inject(HttpClient);
  protected backendInterceptor = inject(BackendInterceptor);
  protected backendFeatureService = inject(BackendFeatureService);
  protected authService = inject(AuthService);
  protected ngZone = inject(NgZone);
  protected environment = inject('environment' as any, { optional: true }) as { [s: string]: any } | null;

  protected readonly streamUrl = 'json/live-events/stream';
  protected readonly subscriptionsUrl = 'json/live-events/subscriptions';
  protected readonly coordinationName = 'gung-live-events';
  protected readonly hiddenGraceMs = 60_000;
  protected readonly subscriptionFlushMs = 50;
  protected readonly collectIntervalMs = 60_000;
  protected readonly maxMissedCollects = 3;
  protected readonly retryBaseMs = 5_000;
  protected readonly retryMaxMs = 5 * 60_000;
  protected readonly maxTopicsQueryLength = 2_000;

  protected readonly tabId = Math.random().toString(36).slice(2) + Date.now().toString(36);
  protected readonly events = new Subject<LiveEvent>();
  protected readonly statusSubject = new BehaviorSubject<LiveEventStatus>('inactive');
  protected readonly topicRefCounts = new Map<string, number>();
  protected readonly topicOrigins = new Map<string, { type: string; key: string }>();

  protected channel: BroadcastChannel | null | undefined;
  protected leader = false;
  protected lockToken: object | null = null;
  protected lockAbort: AbortController | null = null;
  protected releaseLock: (() => void) | null = null;
  protected hiddenSince: number | null = null;
  protected leaderHiddenSince: number | null = null;
  protected stealTimer: ReturnType<typeof setTimeout> | null = null;
  protected flushTimer: ReturnType<typeof setTimeout> | null = null;
  protected visibilityWatched = false;
  protected userWatch: Subscription | null = null;
  protected username: string | null | undefined;

  // Leader only
  protected readonly remoteTabs = new Map<string, RemoteTab>();
  protected readonly appliedTopics = new Set<string>();
  /** Refused by the server (invalid or over its limit); not requested again until the next 'ready'. */
  protected readonly rejectedTopics = new Set<string>();
  protected source: EventSource | null = null;
  protected clientId: string | null = null;
  protected featureActive: boolean | null = null;
  protected featureCheckInFlight = false;
  protected subscriptionInFlight = false;
  protected reconcileAgain = false;
  protected retryAttempt = 0;
  protected retryTimer: ReturnType<typeof setTimeout> | null = null;
  protected subscriptionRetryAttempt = 0;
  protected subscriptionRetryTimer: ReturnType<typeof setTimeout> | null = null;
  protected hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  protected collectTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Emits whenever the entity changes. Any id works, whatever characters it contains.
   * Example: liveEventService.on(LiveEventType.ORDER, orderId).pipe(debounceTime(250)).subscribe(() => this.reload());
   */
  public on(type: LiveEventType, id: string): Observable<LiveEvent> {
    if (!id) {
      return NEVER;
    }
    return this.watch(type, id, `${type}:${escapeLiveEventKey(id)}`);
  }

  /** Emits whenever any entity of the type belonging to the customer changes. */
  public onCustomer(type: LiveEventType, customerId: string): Observable<LiveEvent> {
    if (!customerId) {
      return NEVER;
    }
    return this.watch(type, `customer:${customerId}`, `${type}:customer:${escapeLiveEventKey(customerId)}`);
  }

  /** Emits whenever any entity of the type the user may see changes. */
  public onAll(type: LiveEventType): Observable<LiveEvent> {
    return this.watch(type, '*', `${type}:*`);
  }

  /** For callers that want their own fallback while the stream is not open. */
  public getStatus(): Observable<LiveEventStatus> {
    return this.statusSubject.asObservable().pipe(distinctUntilChanged(), this.insideAngular());
  }

  /** @param key what RESYNC events for the topic carry as key */
  protected watch(type: string, key: string, topic: string): Observable<LiveEvent> {
    return new Observable<LiveEvent>(subscriber => {
      const subscription = this.events
        .pipe(
          filter(event => event.topics?.includes(topic)),
          this.insideAngular()
        )
        .subscribe(subscriber);
      this.topicOrigins.set(topic, { type, key });
      this.retain(topic);
      return () => {
        subscription.unsubscribe();
        this.release(topic);
      };
    });
  }

  /**
   * zone.js patches neither Web Locks nor BroadcastChannel.onmessage, so the leader's stream and the relayed events run
   * outside Angular: subscribers would update their state without change detection ever rendering it.
   */
  protected insideAngular<T>(): MonoTypeOperatorFunction<T> {
    return source =>
      new Observable<T>(subscriber =>
        source.subscribe({
          next: value => this.ngZone.run(() => subscriber.next(value)),
          error: err => this.ngZone.run(() => subscriber.error(err)),
          complete: () => this.ngZone.run(() => subscriber.complete())
        })
      );
  }

  protected retain(topic: string): void {
    const count = this.topicRefCounts.get(topic) ?? 0;
    this.topicRefCounts.set(topic, count + 1);
    if (count === 0) {
      this.localTopicsChanged();
    }
  }

  protected release(topic: string): void {
    const count = this.topicRefCounts.get(topic) ?? 0;
    if (count > 1) {
      this.topicRefCounts.set(topic, count - 1);
      return;
    }
    this.topicRefCounts.delete(topic);
    this.topicOrigins.delete(topic);
    this.localTopicsChanged();
  }

  protected localTopicsChanged(): void {
    if (this.topicRefCounts.size > 0) {
      this.join();
      this.scheduleFlush();
      return;
    }
    if (this.leader) {
      this.leaderTopicsChanged();
      return;
    }
    this.postSnapshot();
    this.resignLeadership();
  }

  protected join(): void {
    if (this.leader || this.lockToken) {
      return;
    }
    if (this.environment?.oauth2Enabled || typeof EventSource === 'undefined') {
      this.setStatus('unavailable');
      return;
    }
    this.watchVisibility();
    this.watchUser();
    this.requestLeadership(false);
  }

  // ---- Leadership ----

  protected requestLeadership(steal: boolean): void {
    const locks = this.getLockManager();
    if (!locks || !this.getChannel()) {
      this.lockToken = {};
      this.becomeLeader();
      return;
    }
    const token = {};
    this.lockToken = token;
    this.lockAbort = steal ? null : new AbortController();
    // The spec rejects steal combined with a signal.
    const options: LockOptions = steal ? { steal: true } : { signal: this.lockAbort.signal };
    locks
      .request(this.coordinationName, options, () => {
        if (this.lockToken !== token) {
          return undefined;
        }
        return new Promise<void>(resolve => {
          this.releaseLock = resolve;
          this.becomeLeader();
        });
      })
      .then(
        () => this.leadershipLost(token),
        () => this.leadershipLost(token)
      );
  }

  /** Stolen, aborted or released: only acts when it wasn't this tab's own decision. */
  protected leadershipLost(token: object): void {
    if (this.lockToken !== token) {
      return;
    }
    this.lockToken = null;
    this.lockAbort = null;
    this.releaseLock = null;
    if (this.leader) {
      this.stepDown();
    }
    if (this.topicRefCounts.size > 0) {
      this.requestLeadership(false);
      this.postSnapshot();
    }
  }

  protected resignLeadership(): void {
    this.lockToken = null;
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.releaseLock?.();
    this.releaseLock = null;
    this.clearStealTimer();
    if (this.leader) {
      this.stepDown();
    }
    this.setStatus('inactive');
  }

  protected becomeLeader(): void {
    this.leader = true;
    this.lockAbort = null;
    this.leaderHiddenSince = null;
    this.clearStealTimer();
    this.post({ kind: 'leader' });
    this.broadcastStatus();
    this.scheduleFlush();
  }

  protected stepDown(): void {
    this.leader = false;
    this.closeSource();
    this.clearRetry();
    this.clearSubscriptionRetry();
    this.clearTimer('hiddenTimer');
    this.stopCollecting();
    this.remoteTabs.clear();
    this.appliedTopics.clear();
    this.rejectedTopics.clear();
    this.subscriptionInFlight = false;
    this.reconcileAgain = false;
    this.setStatus(this.topicRefCounts.size > 0 ? 'connecting' : 'inactive');
  }

  // Hidden tabs may be frozen (mobile, energy saver) and then stop relaying, so a visible tab takes over.
  protected checkSteal(): void {
    this.clearStealTimer();
    if (this.leader || !this.lockToken || this.leaderHiddenSince === null || this.isHidden()) {
      return;
    }
    const remaining = this.hiddenGraceMs - (Date.now() - this.leaderHiddenSince);
    if (remaining > 0) {
      this.stealTimer = setTimeout(() => this.checkSteal(), remaining);
      return;
    }
    this.lockToken = null;
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.requestLeadership(true);
  }

  // ---- Tab coordination ----

  protected getChannel(): BroadcastChannel | null {
    if (this.channel === undefined) {
      this.channel = this.createChannel();
      if (this.channel) {
        this.channel.onmessage = (message: MessageEvent<LiveEventTabMessage>) => this.onTabMessage(message.data);
        window.addEventListener('pagehide', () => {
          if (!this.leader && this.topicRefCounts.size > 0) {
            this.post({ kind: 'topics', tabId: this.tabId, topics: [], visible: false, resync: false });
          }
        });
      }
    }
    return this.channel;
  }

  protected createChannel(): BroadcastChannel | null {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(this.coordinationName);
  }

  protected getLockManager(): LockManager | null {
    return typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : null;
  }

  protected post(message: LiveEventTabMessage): void {
    this.channel?.postMessage(message);
  }

  protected onTabMessage(message: LiveEventTabMessage): void {
    if (this.leader) {
      if (message.kind === 'topics') {
        this.onRemoteTopics(message);
      }
      return;
    }
    switch (message.kind) {
      case 'leader':
        this.leaderHiddenSince = null;
        this.clearStealTimer();
        this.postSnapshot();
        break;
      case 'collect':
        this.postSnapshot();
        break;
      case 'event':
        this.events.next(message.event);
        break;
      case 'resync':
        if (!message.tabId || message.tabId === this.tabId) {
          this.resync(message.topics);
        }
        break;
      case 'status':
        if (this.lockToken) {
          this.statusSubject.next(message.status);
        }
        this.leaderHiddenSince = message.hiddenSince;
        this.checkSteal();
        break;
    }
  }

  protected postSnapshot(resync = false): void {
    if (this.leader || !this.lockToken || !this.channel) {
      return;
    }
    this.post({
      kind: 'topics',
      tabId: this.tabId,
      topics: [...this.topicRefCounts.keys()],
      visible: !this.isHidden(),
      resync
    });
  }

  protected onRemoteTopics(message: Extract<LiveEventTabMessage, { kind: 'topics' }>): void {
    const known = this.remoteTabs.has(message.tabId);
    if (!known && message.topics.length === 0) {
      return;
    }
    if (message.topics.length === 0) {
      this.remoteTabs.delete(message.tabId);
    } else {
      this.remoteTabs.set(message.tabId, {
        topics: new Set(message.topics),
        visible: message.visible,
        missedCollects: 0
      });
    }
    // Topics already active on the server won't get a resync from the subscription flow.
    if ((!known || message.resync) && this.clientId) {
      const active = message.topics.filter(topic => this.appliedTopics.has(topic));
      if (active.length > 0) {
        this.post({ kind: 'resync', topics: active, tabId: message.tabId });
      }
    }
    if (!known) {
      this.broadcastStatus();
    }
    this.syncCollecting();
    this.leaderTopicsChanged();
  }

  // Drops tabs that vanished without saying goodbye (crash, discard).
  protected syncCollecting(): void {
    if (this.remoteTabs.size === 0) {
      this.stopCollecting();
      return;
    }
    this.collectTimer ??= setInterval(() => {
      let dropped = false;
      for (const [tabId, tab] of this.remoteTabs) {
        if (++tab.missedCollects > this.maxMissedCollects) {
          this.remoteTabs.delete(tabId);
          dropped = true;
        }
      }
      this.post({ kind: 'collect' });
      if (dropped) {
        this.syncCollecting();
        this.leaderTopicsChanged();
      }
    }, this.collectIntervalMs);
  }

  protected stopCollecting(): void {
    if (this.collectTimer) {
      clearInterval(this.collectTimer);
      this.collectTimer = null;
    }
  }

  protected wantedTopics(): Set<string> {
    const wanted = new Set(this.topicRefCounts.keys());
    this.remoteTabs.forEach(tab => tab.topics.forEach(topic => wanted.add(topic)));
    return wanted;
  }

  protected anyTabVisible(): boolean {
    return (
      (this.topicRefCounts.size > 0 && !this.isHidden()) ||
      [...this.remoteTabs.values()].some(tab => tab.visible && tab.topics.size > 0)
    );
  }

  protected leaderTopicsChanged(): void {
    if (this.wantedTopics().size === 0) {
      this.resignLeadership();
      return;
    }
    this.syncVisibility();
    this.scheduleFlush();
  }

  // ---- Visibility and user ----

  protected isHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  protected watchVisibility(): void {
    if (this.visibilityWatched || typeof document === 'undefined') {
      return;
    }
    this.visibilityWatched = true;
    this.hiddenSince = this.isHidden() ? Date.now() : null;
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
  }

  protected onVisibilityChange(): void {
    const hiddenFor = this.hiddenSince === null ? 0 : Date.now() - this.hiddenSince;
    this.hiddenSince = this.isHidden() ? (this.hiddenSince ?? Date.now()) : null;
    if (this.leader) {
      this.broadcastStatus();
      this.syncVisibility();
      return;
    }
    if (this.lockToken) {
      this.postSnapshot(!this.isHidden() && hiddenFor > this.hiddenGraceMs);
      this.checkSteal();
    }
  }

  // The stream stays open while any tab with topics is visible, and closes after a grace period otherwise.
  protected syncVisibility(): void {
    if (this.anyTabVisible()) {
      this.clearTimer('hiddenTimer');
      this.ensureConnected();
      return;
    }
    this.hiddenTimer ??= setTimeout(() => {
      this.hiddenTimer = null;
      this.closeSource();
      this.setStatus('inactive');
    }, this.hiddenGraceMs);
  }

  protected watchUser(): void {
    if (this.userWatch) {
      return;
    }
    this.userWatch = this.authService
      .getCurrentUser()
      .pipe(
        map(user => user?.username ?? null),
        distinctUntilChanged()
      )
      .subscribe(username => {
        const changed = this.username !== undefined;
        this.username = username;
        if (!changed) {
          return;
        }
        this.featureActive = null;
        if (!this.leader) {
          return;
        }
        this.closeSource();
        this.clearRetry();
        if (username === null) {
          this.setStatus('inactive');
          return;
        }
        this.ensureConnected();
      });
  }

  // ---- Stream (leader only) ----

  protected ensureConnected(): void {
    if (!this.leader || this.source || this.retryTimer || this.username === null || this.wantedTopics().size === 0) {
      return;
    }
    if (!this.anyTabVisible()) {
      return;
    }
    if (this.featureActive === false) {
      this.setStatus('unavailable');
      return;
    }
    if (this.featureActive === null) {
      this.checkFeature();
      return;
    }
    this.open();
  }

  protected checkFeature(): void {
    if (this.featureCheckInFlight) {
      return;
    }
    this.featureCheckInFlight = true;
    this.backendFeatureService
      .isActivated(LIVE_EVENTS_FEATURE_ID)
      .pipe(
        first(),
        catchError(() => of(false))
      )
      .subscribe(active => {
        this.featureCheckInFlight = false;
        this.featureActive = active;
        this.ensureConnected();
      });
  }

  protected open(): void {
    this.setStatus('connecting');
    const source = new EventSource(this.backendInterceptor.getBaseUrl() + this.streamUrl + this.topicsQuery(), {
      withCredentials: true
    });
    this.source = source;

    // The browser reconnects with the original url, so the applied topics can be stale: reconcile against them.
    source.addEventListener('ready', (raw: MessageEvent) => {
      const ready = this.parse<{ clientId: string; topics?: string[] }>(raw);
      this.clientId = ready?.clientId ?? null;
      this.retryAttempt = 0;
      this.subscriptionInFlight = false;
      this.reconcileAgain = false;
      this.clearSubscriptionRetry();
      this.appliedTopics.clear();
      this.rejectedTopics.clear();
      (ready?.topics ?? []).forEach(topic => this.appliedTopics.add(topic));
      this.setStatus('open');
      this.broadcastResync([...this.appliedTopics]);
      this.reconcile();
    });

    source.addEventListener('change', (raw: MessageEvent) => {
      const event = this.parse<LiveEvent>(raw);
      if (event) {
        this.events.next(event);
        this.post({ kind: 'event', event });
      }
    });

    source.onerror = () => {
      this.clientId = null;
      // CONNECTING: the browser reconnects by itself and a new 'ready' resubscribes.
      // CLOSED: HTTP error (feature missing, limits, auth) - back off and try again later.
      if (source.readyState === EventSource.CLOSED) {
        this.closeSource();
        this.setStatus('unavailable');
        this.scheduleRetry();
      } else {
        this.setStatus('connecting');
      }
    };
  }

  /** As many topics as fit in the url; the rest are added after 'ready'. */
  protected topicsQuery(): string {
    let query = '';
    for (const topic of this.wantedTopics()) {
      let param: string;
      try {
        param = `${query ? '&' : '?'}topics=${encodeURIComponent(topic)}`;
      } catch {
        // Malformed unicode can't go in a url; the topic is added after 'ready' instead.
        continue;
      }
      if (query.length + param.length > this.maxTopicsQueryLength) {
        break;
      }
      query += param;
    }
    return query;
  }

  protected scheduleRetry(): void {
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.ensureConnected();
    }, this.backoff(this.retryAttempt++));
  }

  protected scheduleFlush(): void {
    if (this.flushTimer) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      if (this.leader) {
        this.reconcile();
      } else {
        this.postSnapshot();
      }
    }, this.subscriptionFlushMs);
  }

  /** Brings the server's topics in line with what the tabs want; one request at a time. */
  protected reconcile(): void {
    if (!this.leader) {
      return;
    }
    if (!this.clientId) {
      this.ensureConnected();
      return;
    }
    if (this.subscriptionInFlight || this.subscriptionRetryTimer) {
      this.reconcileAgain = true;
      return;
    }
    const wanted = this.wantedTopics();
    for (const topic of this.rejectedTopics) {
      if (!wanted.has(topic)) {
        this.rejectedTopics.delete(topic);
      }
    }
    const add = [...wanted].filter(topic => !this.appliedTopics.has(topic) && !this.rejectedTopics.has(topic));
    const remove = [...this.appliedTopics].filter(topic => !wanted.has(topic));
    if (add.length === 0 && remove.length === 0) {
      return;
    }
    const clientId = this.clientId;
    this.subscriptionInFlight = true;
    this.http
      .post<{ topics?: string[] } | null>(this.subscriptionsUrl, { clientId, add, remove })
      .pipe(
        first(),
        map(response => ({ success: true, topics: response?.topics })),
        catchError(() => of({ success: false, topics: undefined as string[] | undefined }))
      )
      .subscribe(({ success, topics }) => {
        if (this.clientId !== clientId) {
          return;
        }
        this.subscriptionInFlight = false;
        if (!success) {
          this.subscriptionRetryTimer = setTimeout(() => {
            this.subscriptionRetryTimer = null;
            this.reconcile();
          }, this.backoff(this.subscriptionRetryAttempt++));
          return;
        }
        this.subscriptionRetryAttempt = 0;
        remove.forEach(topic => this.appliedTopics.delete(topic));
        const applied = topics ? new Set(topics) : null;
        const added = applied ? add.filter(topic => applied.has(topic)) : add;
        added.forEach(topic => this.appliedTopics.add(topic));
        if (applied) {
          add.filter(topic => !applied.has(topic)).forEach(topic => this.rejectedTopics.add(topic));
        }
        // Only after the server acknowledged the topics, so nothing can slip between the refetch and the subscription.
        this.broadcastResync(added);
        if (this.reconcileAgain) {
          this.reconcileAgain = false;
          this.reconcile();
        }
      });
  }

  protected broadcastResync(topics: string[]): void {
    if (topics.length === 0) {
      return;
    }
    this.resync(topics);
    this.post({ kind: 'resync', topics });
  }

  protected resync(topics: string[]): void {
    const timestamp = new Date().toISOString();
    for (const topic of topics) {
      const origin = this.topicOrigins.get(topic);
      if (!origin) {
        continue;
      }
      this.events.next({
        ...origin,
        action: 'RESYNC',
        timestamp,
        topics: [topic]
      });
    }
  }

  protected closeSource(): void {
    this.source?.close();
    this.source = null;
    this.clientId = null;
  }

  protected clearRetry(): void {
    this.clearTimer('retryTimer');
    this.retryAttempt = 0;
  }

  protected clearSubscriptionRetry(): void {
    this.clearTimer('subscriptionRetryTimer');
    this.subscriptionRetryAttempt = 0;
  }

  protected clearStealTimer(): void {
    this.clearTimer('stealTimer');
  }

  protected clearTimer(
    name: 'retryTimer' | 'subscriptionRetryTimer' | 'hiddenTimer' | 'stealTimer' | 'flushTimer'
  ): void {
    if (this[name]) {
      clearTimeout(this[name]);
      this[name] = null;
    }
  }

  protected backoff(attempt: number): number {
    const delay = Math.min(this.retryMaxMs, this.retryBaseMs * 2 ** attempt);
    return delay * (0.5 + Math.random() * 0.5);
  }

  protected setStatus(status: LiveEventStatus): void {
    this.statusSubject.next(status);
    this.broadcastStatus();
  }

  protected broadcastStatus(): void {
    if (this.leader) {
      this.post({ kind: 'status', status: this.statusSubject.value, hiddenSince: this.hiddenSince });
    }
  }

  protected parse<T>(raw: MessageEvent): T | null {
    try {
      return JSON.parse(raw.data) as T;
    } catch {
      return null;
    }
  }
}
